const FREE_EDITOR_BASE = "https://editor-embedded.pfpmaker.com";

export const FREE_PFP_DEMO_CUTOUT = `${FREE_EDITOR_BASE}/demo/demo1.png`;

export type FreePfpKind = "color" | "gradient" | "photo";

export interface FreePfpResult {
  id: string;
  label: string;
  kind: FreePfpKind;
  /** CSS color or gradient (kind=color | gradient) */
  fill?: string;
  /** Photo background URL (kind=photo) */
  backgroundUrl?: string;
}

export interface FreePfpSwatch {
  id: string;
  kind: "color" | "gradient";
  fill: string;
  label: string;
}

/** Original pfpmaker-webapp `src/data/colors.ts` — 14 solids. */
const ORIGINAL_COLORS: Array<{ hex: string; label: string }> = [
  { hex: "#c3c8d7", label: "Gray" },
  { hex: "#667c89", label: "Slate" },
  { hex: "#ed6337", label: "Coral" },
  { hex: "#FF416C", label: "Pink" },
  { hex: "#ff8900", label: "Orange" },
  { hex: "#ffe400", label: "Yellow" },
  { hex: "#a5ea20", label: "Lime" },
  { hex: "#60f6ad", label: "Mint" },
  { hex: "#46e6d1", label: "Aqua" },
  { hex: "#bae1ff", label: "Sky" },
  { hex: "#4c89f8", label: "Blue" },
  { hex: "#7844e9", label: "Purple" },
  { hex: "#3f2b96", label: "Indigo" },
  { hex: "#cd5aec", label: "Magenta" },
];

/** Original pfpmaker-webapp `src/data/gradients.ts` — 42 stops, `to right`. */
const ORIGINAL_GRADIENTS: Array<{ id: string; stops: string; label: string }> = [
  { id: "portrait-gray", stops: "#8E9EAB, #EEF2F3", label: "Portrait Gray" },
  { id: "february", stops: "#ACCBEE, #E7F0FD", label: "February" },
  { id: "soft-purple", stops: "#D3CCE3, #E9E4F0", label: "Soft Purple" },
  { id: "soft-yellow-pink", stops: "#D9A7C7, #FFFCDC", label: "Soft Yellow Pink" },
  { id: "unicorn", stops: "#FBC8D4, #9795F0", label: "Unicorn" },
  { id: "orange-grad", stops: "#FFE259, #FFA751", label: "Orange" },
  { id: "orange-yellow", stops: "#F7971E, #FFD200", label: "Orange Yellow" },
  { id: "phoenix", stops: "#F83600, #F9D423", label: "Phoenix" },
  { id: "light-pink", stops: "#EE9CA7, #FFDDE1", label: "Light Pink" },
  { id: "mean-fruit", stops: "#FCCB90, #D57EEB", label: "Mean Fruit" },
  { id: "passion", stops: "#FF8177, #B12A5B", label: "Passion" },
  { id: "orange-pink", stops: "#F2709C, #FF9472", label: "Orange Pink" },
  { id: "peach", stops: "#ED4264, #FFEDBC", label: "Peach" },
  { id: "lavish", stops: "#FF44C5, #FFF1A3", label: "Lavish" },
  { id: "pink-grad", stops: "#EC008C, #FC6767", label: "Pink" },
  { id: "royal", stops: "#7900FF, #FF58CA", label: "Royal" },
  { id: "storm", stops: "#B721FF, #21D4FD", label: "Storm" },
  { id: "deep-blue", stops: "#6A11CB, #77D4FF", label: "Deep Blue" },
  { id: "malibu", stops: "#2B98F8, #00FEA3", label: "Malibu" },
  { id: "blue-light-blue", stops: "#5433FF, #20BDFF, #A5FECB", label: "Blue Light Blue" },
  { id: "sulphur", stops: "#CAC531, #F3F9A7", label: "Sulphur" },
  { id: "limeade", stops: "#A1FFCE, #FAFFD1", label: "Limeade" },
  { id: "mojito", stops: "#1D976C, #93F9B9", label: "Mojito" },
  { id: "green-yellow", stops: "#FFE000, #799F0C", label: "Green Yellow" },
  { id: "lush-green", stops: "#56AB2F, #A8E063", label: "Lush Green" },
  { id: "fresh", stops: "#429321, #B4EC51", label: "Fresh" },
  { id: "tropical", stops: "#16A085, #F4D03F", label: "Tropical" },
  { id: "meadow", stops: "#4EF7FF, #FFCD1B", label: "Meadow" },
  { id: "full-moon", stops: "#5EE7DF, #B490CA", label: "Full Moon" },
  { id: "cloudy", stops: "#BAC8E0, #6A85B6", label: "Cloudy" },
  { id: "winter-eve", stops: "#A1C4FD, #C2E9FB", label: "Winter Eve" },
  { id: "plum", stops: "#667EEA, #764BA2", label: "Plum" },
  { id: "expresso", stops: "#AD5389, #3C1053", label: "Expresso" },
  { id: "lagoon", stops: "#66A6FF, #89F7FE", label: "Lagoon" },
  { id: "venice-blue", stops: "#6190E8, #A7BFE8", label: "Venice Blue" },
  { id: "mesosphere", stops: "#473B7B, #30D2BE", label: "Mesosphere" },
  { id: "dark-blue", stops: "#1488CC, #2B32B2", label: "Dark Blue" },
  { id: "under-the-lake", stops: "#093028, #237A57", label: "Under the Lake" },
  { id: "twitch", stops: "#6441A5, #2A0845", label: "Twitch" },
  { id: "midnight-city", stops: "#232526, #414345", label: "Midnight City" },
  { id: "moonlit-asteroid", stops: "#0F2027, #203A43, #2C5364", label: "Moonlit Asteroid" },
  { id: "royal-blue", stops: "#141E30, #243B55", label: "Royal Blue" },
];

/** Original pfpmaker-webapp `src/data/backgrounds.ts` — 24 photos. */
const ORIGINAL_PHOTOS: Array<{ id: string; file: string; label: string }> = [
  { id: "beach1", file: "beach1.jpg", label: "Beach 1" },
  { id: "beach2", file: "beach2.jpg", label: "Beach 2" },
  { id: "beach3", file: "beach3.jpg", label: "Beach 3" },
  { id: "beach4", file: "beach4.jpg", label: "Beach 4" },
  { id: "beach5", file: "beach5.jpg", label: "Beach 5" },
  { id: "beach6", file: "beach6.jpg", label: "Beach 6" },
  { id: "bokeh1", file: "bokeh1.jpg", label: "Bokeh 1" },
  { id: "bokeh2", file: "bokeh2.jpg", label: "Bokeh 2" },
  { id: "bokeh3", file: "bokeh3.jpg", label: "Bokeh 3" },
  { id: "bokeh4", file: "bokeh4.jpg", label: "Bokeh 4" },
  { id: "glow1", file: "glow1.jpg", label: "Glow 1" },
  { id: "glow2", file: "glow2.jpg", label: "Glow 2" },
  { id: "glow3", file: "glow3.jpg", label: "Glow 3" },
  { id: "office1", file: "office1.jpg", label: "Office 1" },
  { id: "office2", file: "office2.jpg", label: "Office 2" },
  { id: "office3", file: "office3.jpg", label: "Office 3" },
  { id: "office4", file: "office4.jpg", label: "Office 4" },
  { id: "office5", file: "office5.jpg", label: "Office 5" },
  { id: "office6", file: "office6.jpg", label: "Office 6" },
  { id: "office7", file: "office7.jpg", label: "Office 7" },
  { id: "office8", file: "office8.jpg", label: "Office 8" },
  { id: "office9", file: "office9.jpg", label: "Office 9" },
  { id: "park1", file: "park1.jpg", label: "Park 1" },
  { id: "park2", file: "park2.jpg", label: "Park 2" },
];

export const FREE_PFP_SWATCHES: FreePfpSwatch[] = [
  ...ORIGINAL_COLORS.map((c) => ({
    id: c.hex.replace("#", "").toLowerCase(),
    kind: "color" as const,
    fill: c.hex,
    label: c.label,
  })),
  ...ORIGINAL_GRADIENTS.map((g) => ({
    id: g.id,
    kind: "gradient" as const,
    fill: `linear-gradient(to right, ${g.stops})`,
    label: g.label,
  })),
];

export const FREE_PFP_PHOTOS: FreePfpResult[] = ORIGINAL_PHOTOS.map((p) => ({
  id: `bg-${p.id}`,
  label: p.label,
  kind: "photo",
  backgroundUrl: `${FREE_EDITOR_BASE}/assets/backgrounds/${p.file}`,
}));

/** Leading looks in the preset row (full catalogs live in Background). */
const RESULT_SWATCH_IDS = ["c3c8d7", "7844e9", "ed6337", "winter-eve", "plum"];
const RESULT_PHOTO_IDS = ["bg-office1", "bg-bokeh1", "bg-office2", "bg-bokeh2", "bg-office3", "bg-bokeh3"];

export function buildFreePfpResults(): FreePfpResult[] {
  const colorResults: FreePfpResult[] = RESULT_SWATCH_IDS.flatMap((id) => {
    const s = FREE_PFP_SWATCHES.find((sw) => sw.id === id);
    if (!s) return [];
    return [
      {
        id: `swatch-${s.id}`,
        label: s.label,
        kind: s.kind,
        fill: s.fill,
      },
    ];
  });

  const photoResults = RESULT_PHOTO_IDS.flatMap((id) => {
    const p = FREE_PFP_PHOTOS.find((photo) => photo.id === id);
    return p ? [p] : [];
  });
  return [...colorResults, ...photoResults];
}

export function isPresetSolidFill(fill: string | undefined): boolean {
  if (!fill) return false;
  const needle = fill.toLowerCase();
  return FREE_PFP_SWATCHES.some((s) => s.kind === "color" && s.fill.toLowerCase() === needle);
}
